import React, { useCallback, useEffect, useRef } from "react";

/**
 * A lightweight, dependency-free popup viewer for the photo and video
 * galleries. It shows one item at a time over a dark backdrop and lets the
 * visitor move left/right - with the arrows, the keyboard, or a swipe on a
 * phone. `items` is an array of { type: "image" | "video", src, title }.
 */
export default function Lightbox({ items, index, onClose, onNav }) {
  const total = items?.length || 0;
  const item = total ? items[index] : null;

  const go = useCallback(
    (step) => {
      if (!total) return;
      onNav((index + step + total) % total);
    },
    [index, total, onNav]
  );

  // Keyboard: Esc closes, arrows move. Also stop the page behind from
  // scrolling while the viewer is open.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [go, onClose]);

  // Swipe on touch devices.
  const touchX = useRef(null);
  const onTouchStart = (e) => {
    touchX.current = e.changedTouches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  if (!item) return null;

  return (
    <div className="lb-overlay" onClick={onClose}>
      <button className="lb-close" onClick={onClose} aria-label="Close">
        &times;
      </button>

      {total > 1 && (
        <button
          className="lb-nav lb-prev"
          onClick={(e) => {
            e.stopPropagation();
            go(-1);
          }}
          aria-label="Previous"
        >
          &#10094;
        </button>
      )}

      <div
        className="lb-stage"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {item.type === "video" ? (
          <div className="lb-video">
            <iframe
              src={item.src}
              title={item.title || "video"}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : (
          <img className="lb-img" src={item.src} alt={item.title || ""} />
        )}
        {(item.title || total > 1) && (
          <div className="lb-caption">
            <span>{item.title}</span>
            {total > 1 && (
              <span className="lb-count">
                {index + 1} / {total}
              </span>
            )}
          </div>
        )}
      </div>

      {total > 1 && (
        <button
          className="lb-nav lb-next"
          onClick={(e) => {
            e.stopPropagation();
            go(1);
          }}
          aria-label="Next"
        >
          &#10095;
        </button>
      )}
    </div>
  );
}
